import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import * as React from 'react';
import { Badge } from './badge';
import { Input } from './input';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  readOnly?: string[];
  maxLength?: number;
  maxTags?: number;
  onDeleteTag?: (tag: string) => void;
}

export function TagInput({
  value,
  onChange,
  onBlur,
  placeholder = 'Press enter to add...',
  readOnly = [],
  maxLength = 30,
  maxTags = 20,
  onDeleteTag,
}: TagInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = (index: number) => {
    const tag = value[index];
    if (readOnly.includes(tag)) return;

    if (onDeleteTag) {
      onDeleteTag(tag);
    } else {
      const newTags = value.filter((_, i) => i !== index);
      onChange(newTags);
    }
    setError(null);
  };

  const validateTag = (tag: string): string | null => {
    if (tag.length === 0) return 'Tag cannot be empty';
    if (tag.length > maxLength) return `Tag must be less than ${maxLength} characters`;
    if (value.includes(tag)) return 'Tag already exists';
    if (value.length >= maxTags) return `Maximum ${maxTags} tags allowed`;
    return null;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue) {
      e.preventDefault();

      const validationError = validateTag(inputValue);
      if (validationError) {
        setError(validationError);
        return;
      }

      onChange([...value, inputValue.trim()]);
      setInputValue('');
      setError(null);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      const lastTag = value[value.length - 1];
      if (!readOnly.includes(lastTag)) {
        handleDelete(value.length - 1);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setError(null);
  };

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'flex flex-wrap gap-2 rounded-md border bg-background p-2',
          error && 'border-destructive'
        )}
      >
        {value.map((tag, index) => (
          <Badge
            key={tag}
            variant={readOnly.includes(tag) ? 'outline' : 'default'}
            className={cn('text-sm', readOnly.includes(tag) && 'cursor-default')}
          >
            {tag}
            {!readOnly.includes(tag) && (
              <button
                type="button"
                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={() => handleDelete(index)}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Remove {tag}</span>
              </button>
            )}
          </Badge>
        ))}
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          type="text"
          placeholder={placeholder}
          className="flex-1 !border-0 !ring-0 !ring-offset-0 focus-visible:!ring-0 px-0 py-0 min-w-[120px] placeholder:text-muted-foreground"
          maxLength={maxLength}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
